import React, { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPhone, faPhoneSlash, faVideo } from '@fortawesome/free-solid-svg-icons';
import { Message } from '../../../../services/chatService/ChatService';
import { formatSystemMessage } from '../../../../utilities/formatSystemMessage';
import { FiPhoneCall } from "react-icons/fi";
import { HiOutlinePhoneMissedCall } from "react-icons/hi";
import { CiVideoOn } from "react-icons/ci";



interface CallLogMessageProps {
    messages: Message[];
}

const PhoneCall = FiPhoneCall as any;
const MissedCall = HiOutlinePhoneMissedCall as any;
const VideoOn = CiVideoOn as any;


const CallLogMessage: React.FC<CallLogMessageProps> = ({ messages }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    // Analyze the group to determine the "Main Status"
    const { title, icon, colorClass, duration } = useMemo(() => {
        const texts = messages.map(m => m.messageContent.toLowerCase());
        const hasEnded = texts.some(t => t.includes('ended'));
        const hasRejected = texts.some(t => t.includes('rejected') || t.includes('missed'));
        const hasStarted = texts.some(t => t.includes('started') || t.includes('calling'));

        // Find duration string in the "Ended" message if available
        // Assuming format "Call Ended in 32 sec"
        const endedMsg = messages.find(m => m.messageContent.toLowerCase().includes('ended'));
        let durationStr = "";
        if (endedMsg) {
            const match = endedMsg.messageContent.match(/in (.+?)( -|$)/);
            if (match) durationStr = match[1];
        }

        if (hasRejected) {
            return { title: "Missed Call", icon: faPhoneSlash, colorClass: "text-red-500 bg-red-50", duration: "" };
        }
        if (hasEnded) {
            return { title: "Video Call Ended", icon: faVideo, colorClass: "text-gray-600 bg-[#F9FAFB]", duration: durationStr };
        }

        // 2. Determine Title based on content
        if (hasRejected) {
            return { title: "Missed Call", icon: faPhoneSlash, colorClass: "text-red-500 bg-red-50 border-red-200", duration: "" };
        }
        if (hasEnded) {
            return { title: "Video Call Ended", icon: faVideo, colorClass: "text-gray-600 bg-[#F9FAFB] border-gray-200", duration: durationStr };
        }
        // If this block has neither a start nor an end, it's just a middle update
        if (!hasStarted) {
            return { title: "Call Update", icon: faPhone, colorClass: "text-blue-600 bg-blue-50 border-blue-200", duration: "" };
        }

        return { title: "Call in Progress...", icon: faPhone, colorClass: "text-green-600 bg-green-50 border-green-200", duration: "" };
    }, [messages]);

    return (
        <div className="flex my-2 w-full">
            <div className={`rounded-lg p-3 w-3/4 max-w-sm border border-gray-200 ${colorClass}`}>
                {/* Header Section */}
                <div
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => setIsExpanded(!isExpanded)}
                >
                    <div className="flex items-center gap-3">
                        <div className="">
                            {/* <FontAwesomeIcon icon={icon} className="text-lg" /> */}
                            <div className="">
                                {title === "Call Update" || title === "Call in Progress..." && <PhoneCall className="text-2xl" />}
                                {title === "Missed Call" && <MissedCall className="text-2xl" />}
                                {title === "Video Call Ended" && <VideoOn className="text-2xl text-[#7E57C2]" />}
                            </div>

                        </div>
                        <div className="flex flex-col items-start">
                            <span className="font-semibold text-sm">{title}</span>
                            {duration && <span className="text-xs opacity-75">{duration}</span>}
                            {!duration && <span className="text-xs opacity-75">{messages.length} updates</span>}
                        </div>
                    </div>
                </div>

                {/* Expanded Details Section */}
                {isExpanded && (
                    <div className="mt-3 pt-2 border-t border-gray-300/50 space-y-1">
                        {messages.map((msg) => (
                            <div key={msg.id} className="text-xs text-gray-600 flex justify-between">
                                <span>{formatSystemMessage(msg.messageContent).split('·')[0]}</span>
                                <span className="opacity-50 text-[10px]">
                                    {new Date(msg.createDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CallLogMessage;